import React, { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Backdrop,
    CircularProgress,
    Button,
    Typography,
    Grid2,
    IconButton,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    Table,
} from '@mui/material';
import { RootState } from '../../redux/reducers';
import { importOrders } from '../../redux/actions/orderActions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import OrdersTable from './OrdersTable';
import OrderForm from './forms/OrderForm';
import { CompleteOrderType } from '../../common/Types';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import DemandOrderForm from './forms/DemandOrderForm';
import ReturnOrderForm from './forms/ReturnOrderForm';
import { isEmployeeWarehouseOwnerOnly } from '../../redux/selectors/employeeSelector';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { DemandOrderDto, OrderStatus, ReturnOrderDto, SpecializedOrderStatus } from '../../api/ClientApi';
import { WarehouseClient } from '../../api/Extensions';
import OrderInfoExtended from './OrderInfoExtended';

export default function OrdersDetails(): ReactElement {
    const orders = useAppSelector((state: RootState) => state.orders.orders);
    const [demandOrder, setDemandOrder] = useState<DemandOrderDto | undefined>(undefined);
    const [returnOrder, setReturnOrder] = useState<ReturnOrderDto | undefined>(undefined);

    const isHighestPrivilageWarehouseOwner = useAppSelector(isEmployeeWarehouseOwnerOnly);

    const location = useLocation();
    const path = location.pathname;

    const params = useParams();

    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    useEffect(() => {
        const demandOrderId = params.demandOrderId;
        const returnOrderId = params.returnOrderId;

        if (
            !!path &&
            path.indexOf('/demandOrder') !== -1 &&
            !!demandOrderId &&
            !isNaN(+demandOrderId) &&
            +demandOrderId > 0
        ) {
            WarehouseClient.getDemandOrder(+demandOrderId).then((res) => {
                setDemandOrder(res);
                setOrderType(CompleteOrderType.Demand);
            });
        } else if (
            !!path &&
            path.indexOf('/returnOrder') !== -1 &&
            !!returnOrderId &&
            !isNaN(+returnOrderId) &&
            +returnOrderId > 0
        ) {
            WarehouseClient.getReturnOrder(+returnOrderId).then((res) => {
                setReturnOrder(res);
                setOrderType(CompleteOrderType.GeneralReturn);
            });
        }
    }, [dispatch, path, params]);

    useEffect(() => {
        if (!orders) {
            dispatch(importOrders());
        }
    }, [dispatch]);

    const handleClose = () => {
        navigate('../../');
    };

    const [openOrderForm, setOpenOrderForm] = useState<boolean>(false);
    const [openDemandOrderForm, setOpenDemandOrderForm] = useState<boolean>(false);
    const [openReturnOrderForm, setOpenReturnOrderForm] = useState<boolean>(false);
    const [orderType, setOrderType] = React.useState<CompleteOrderType | undefined>(undefined);

    const goToForm = () => {
        setOpenOrderForm(true);
    };

    const handleCloseOrderForm = () => {
        setOpenOrderForm(false);
    };

    const goToDemandOrderForm = () => {
        setOpenDemandOrderForm(true);
    };

    const handleCloseDemandOrderForm = () => {
        setOpenDemandOrderForm(false);
    };

    const goToReturnOrderForm = () => {
        setOpenReturnOrderForm(true);
    };

    const handleCloseReturnOrderForm = () => {
        setOpenReturnOrderForm(false);
    };

    const currOrders = useMemo(() => {
        return !!demandOrder
            ? orders?.filter((o) => o.demandOrderId === demandOrder.id)
            : !!returnOrder
              ? orders?.filter((o) => o.returnOrderId === returnOrder.id)
              : [];
    }, [demandOrder, orders, returnOrder]);

    const canCreateNewOrder =
        !isHighestPrivilageWarehouseOwner &&
        (demandOrder?.demandOrderStatus === SpecializedOrderStatus.Created ||
            demandOrder?.demandOrderStatus === SpecializedOrderStatus.InProgress ||
            returnOrder?.returnOrderStatus === SpecializedOrderStatus.Created ||
            returnOrder?.returnOrderStatus === SpecializedOrderStatus.InProgress);

    const canChangeOrder =
        !isHighestPrivilageWarehouseOwner &&
        (demandOrder?.demandOrderStatus === SpecializedOrderStatus.Created ||
            returnOrder?.returnOrderStatus === SpecializedOrderStatus.Created);

    type ArticleQuantityType = {
        article: string;
        quantity: number;
        alreadyOrderedQuantity?: number;
    };

    const orderedItems = useMemo(() => {
        const orderGoodsItems =
            orderType === CompleteOrderType.Demand
                ? demandOrder?.orderGoodsItems
                : orderType === CompleteOrderType.GeneralReturn
                  ? returnOrder?.orderGoodsItems
                  : [];

        const alreadyOrderedItems =
            orderType === CompleteOrderType.Demand
                ? (demandOrder?.orders
                      ?.filter((o) => o.orderStatus !== OrderStatus.Cancelled && o.orderStatus !== OrderStatus.Returned)
                      .map((o) => o.orderGoodsItems ?? [])
                      .flat() ?? [])
                : orderType === CompleteOrderType.GeneralReturn
                  ? (returnOrder?.orders
                        ?.filter(
                            (o) => o.orderStatus !== OrderStatus.Cancelled && o.orderStatus !== OrderStatus.Returned,
                        )
                        .map((o) => o.orderGoodsItems ?? [])
                        .flat() ?? [])
                  : [];
        const art = !!orderGoodsItems
            ? orderGoodsItems
                  ?.filter((g) => !!g.article?.title && !!g.quantity)
                  .map((g) => {
                      return { article: g.article, quantity: g.quantity ?? 0 };
                  })
            : [];

        const artUnique: ArticleQuantityType[] = [];

        art.map((a) => {
            const uniqueKey =
                (a.article?.title ?? '') +
                ', ' +
                (a.article?.manufacturer ?? '') +
                ' (' +
                (a.article?.innerCode ?? '') +
                ')';
            const oldArtIndex = artUnique.findIndex((au) => au.article === uniqueKey);
            if (oldArtIndex === undefined || oldArtIndex === -1) {
                const alreadyOrderedQuantity =
                    orderType === CompleteOrderType.Demand || orderType === CompleteOrderType.GeneralReturn
                        ? (alreadyOrderedItems
                              ?.filter((aoi) => aoi.articleId === a.article?.id)
                              .reduce((acc, val) => {
                                  return (acc += val.quantity ?? 0);
                              }, 0) ?? undefined)
                        : undefined;

                const aCopy: ArticleQuantityType = {
                    article: uniqueKey,
                    quantity: a.quantity,
                    alreadyOrderedQuantity: alreadyOrderedQuantity,
                };

                artUnique.push(aCopy);
            } else {
                artUnique[oldArtIndex].quantity += a.quantity;
            }
        });

        return artUnique;
    }, [orderType, returnOrder, demandOrder, orders]);

    return orders === null || (!demandOrder && !returnOrder) ? (
        <Fragment>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!Boolean(orders)}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                <Grid2>
                    <Box sx={{ display: 'flex' }}>
                        <IconButton color="inherit" onClick={handleClose} sx={{ p: 0 }}>
                            <ArrowBackIosIcon />
                        </IconButton>
                        <Typography variant="h5">
                            {!!demandOrder ? 'Trebovanje' : !!returnOrder ? 'Povratnica' : '/'}
                        </Typography>
                    </Box>
                </Grid2>
                <Grid2>
                    <Grid2 container sx={{ gap: 1 }}>
                        {canChangeOrder && (
                            <Button
                                color="warning"
                                variant="contained"
                                onClick={() =>
                                    orderType === CompleteOrderType.Demand
                                        ? goToDemandOrderForm()
                                        : orderType === CompleteOrderType.GeneralReturn
                                          ? goToReturnOrderForm()
                                          : console.log('NO FORM')
                                }
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Izmeni {orderType === CompleteOrderType.Demand && 'trebovanje'}{' '}
                                {orderType === CompleteOrderType.GeneralReturn && 'povratnicu'}
                            </Button>
                        )}
                        {canCreateNewOrder && (
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={goToForm}
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Novi nalog
                            </Button>
                        )}
                    </Grid2>
                </Grid2>
            </Grid2>
            <Box sx={{ mt: 2 }} />
            <OrderInfoExtended demandOrder={demandOrder} returnOrder={returnOrder} />
            <Box sx={{ margin: 2 }}>
                <Grid2 sx={{ textAlign: 'start', mb: 2 }}>
                    <Typography variant="h5">Roba</Typography>
                </Grid2>
                <Table size="small" aria-label="purchases">
                    <TableHead>
                        <TableRow>
                            <TableCell>Artikal</TableCell>
                            <TableCell align="center">Količina</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orderedItems?.map((article) => {
                            return (
                                <TableRow
                                    key={article.article}
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                    }}
                                >
                                    <TableCell component="th" scope="row">
                                        {article.article ?? '/'}
                                    </TableCell>
                                    <TableCell align="center">
                                        <span style={{ opacity: 0.5 }}>
                                            {(article.alreadyOrderedQuantity ?? 0).toString()}/
                                        </span>
                                        {(article.quantity ?? '/').toString()}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            <Box sx={{ p: 4, pt: 2, mt: 4 }}>
                <Box sx={{ p: 0, mt: 2 }}></Box>

                <DetailsTableContainerBox>
                    <OrdersTable orders={currOrders ?? null} returnOrders={null} demandOrders={null} />
                </DetailsTableContainerBox>
            </Box>
            <OrderForm
                closeForm={handleCloseOrderForm}
                open={openOrderForm}
                orderId={undefined}
                demandOrderId={demandOrder?.id}
                returnOrderId={returnOrder?.id}
                originOrderType={orderType}
            />
            <DemandOrderForm
                closeForm={handleCloseDemandOrderForm}
                open={openDemandOrderForm}
                demandOrderId={demandOrder?.id}
            />
            <ReturnOrderForm
                closeForm={handleCloseReturnOrderForm}
                open={openReturnOrderForm}
                returnOrderId={returnOrder?.id}
            />
        </DetailsContainerBox>
    );
}
